import { useState } from 'react';
import { processPayment } from '@/services/PaymentService';

interface WrappedPayPalButtonProps {
  sessionId: string;
  disabled?: boolean;
  onError?: (error: string) => void;
}

/**
 * Wrapped PayPal Button - Simulates native app flow
 * This button triggers the galleon-wrapped-paypal-vaulted flow which:
 * 1. Calls backend with wrapped flow config
 * 2. Backend returns redirect URL with flow=wrapped_paypal_vaulted
 * 3. User sees minimal "Redirecting..." UI
 * 4. Auto-triggers PayPal vaulted flow
 * 5. After PayPal, redirects back to native app (simulated via return_url)
 */
const WrappedPayPalButton = ({ sessionId, disabled = false, onError }: WrappedPayPalButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleWrappedPayPalPayment = async () => {
    if (disabled || isLoading) return;

    try {
      setIsLoading(true);

      // Backup current JWT token
      const currentToken = sessionStorage.getItem('token');
      if (currentToken && currentToken.startsWith('eyJ')) {
        sessionStorage.setItem('original_jwt_token', currentToken);
      }

      const chargeRequest = {
        session_id: sessionId,
        save_payment_method: false,
        is_new_payment_method: true,
        payment_method: {
          data: {
            provider: {
              provider: 'custom',
              config: {
                flow: 'galleon-wrapped-paypal-vaulted'
              }
            }
          }
        }
        // Note: Not setting return_url for demo/test button - it's for browser testing only
        // Real native apps should pass their deeplink (e.g., "myapp://payment-complete")
      };

      const response = await processPayment(chargeRequest);

      if (response.next_actions?.length > 0) {
        for (const action of response.next_actions) {
          if (action.action === 'open_url' && action.parameters?.url) {
            sessionStorage.removeItem('wrapped_paypal_triggered');
            window.location.href = action.parameters.url as string;
            return;
          }
        }
      }

      throw new Error('No redirect URL received from server');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Wrapped PayPal payment failed';
      onError?.(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleWrappedPayPalPayment}
      disabled={disabled || isLoading}
      className={`
        w-full h-12 rounded-lg font-medium text-sm
        flex items-center justify-center gap-2
        transition-all duration-200
        ${disabled || isLoading 
          ? 'bg-gray-400 cursor-not-allowed text-gray-600' 
          : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 active:from-purple-700 active:to-pink-700 text-white'
        }
      `}
    >
      {isLoading ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Processing...</span>
        </>
      ) : (
        <>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <span>🧪 Test Native PayPal Flow</span>
        </>
      )}
    </button>
  );
};

export default WrappedPayPalButton;



